import { supabase } from '@/integrations/supabase/client';

// Simplified icon categories using only existing icons in Supabase Storage
export const SUPABASE_ICONS = {
  // Core application icons
  lyraAvatar: 'lyra-avatar.png',
  heroMain: 'hero-main.png',
  dashboardMeditation: 'dashboard-meditation.png',
  dashboardRocketMp4: 'rocket-animated.mp4',
  dashboardRocketGif: 'rocket-animated.gif',
  
  // Navbar and branding (using uploaded navbar logo)
  navbar: {
    logo: 'navbar-logo.png',
    logoCompact: 'navbar-logo.png'
  },
  
  // Lyra expressions (using fallbacks to existing icons)
  lyra: {
    default: 'lyra-avatar.png',
    thinking: 'lyra-avatar.png', // fallback to default since thinking specific might not exist
    celebrating: 'lyra-avatar.png', // fallback to default
    helping: 'lyra-avatar.png', // fallback to default
    loading: 'lyra-avatar.png'
  },
  
  // User role specific avatars
  userRoles: {
    fundraising: 'user-role-fundraising.png',
    programs: 'user-role-programs.png',
    operations: 'user-role-operations.png',
    marketing: 'user-role-marketing.png',
    it: 'user-role-it.png',
    leadership: 'user-role-leadership.png',
    other: 'user-role-leadership.png' // use leadership for other roles
  },
  
  // Chapter icons (mapped to existing icons)
  chapters: {
    1: 'lyra-avatar.png',
    2: 'learning-target.png',
    3: 'data-analytics.png',
    4: 'mission-heart.png',
    5: 'network-connection.png',
    6: 'workflow-process.png',
    7: 'data-analytics.png',
    8: 'communication.png',
    9: 'achievement-trophy.png',
    10: 'growth-plant.png'
  },
  
  // UI state icons
  uiStates: {
    loading: 'lyra-avatar.png',
    errorFriendly: 'lyra-avatar.png',
    successCompletion: 'badge-course-complete.png',
    emptyWelcome: 'navbar-logo.png', // fallback to known working icon
    lockState: 'lyra-avatar.png'
  },
  
  // Achievement badges
  achievements: {
    firstChapter: 'badge-first-chapter.png',
    courseComplete: 'badge-course-complete.png',
    profileComplete: 'achievement-trophy.png'
  },
  
  // Onboarding and progress icons (using existing icons as fallbacks)
  onboarding: {
    profileCompletion: 'navbar-logo.png', // changed from non-existent onboarding-profile.png
    welcome: 'navbar-logo.png', // fallback to known working icon
    progress: 'learning-target.png' // changed from chapter-progress.png
  },
  
  // Specialty AI Tools icons
  specialtyTools: {
    perplexityAi: 'data-analytics.png', // Research & analysis icon
    cursor: 'communication.png', // Code development icon 
    lovable: 'mission-heart.png', // App building with heart
    replit: 'workflow-process.png', // Collaborative programming
    midjourney: 'growth-plant.png' // Creative visual content
  },

  // Feature icons (all existing)
  features: {
    learningTarget: 'learning-target.png',
    missionHeart: 'mission-heart.png',
    achievementTrophy: 'achievement-trophy.png',
    networkConnection: 'network-connection.png',
    workflowProcess: 'workflow-process.png',
    dataAnalytics: 'data-analytics.png',
    communication: 'communication.png',
    growthPlant: 'growth-plant.png',
    successCelebration: 'achievement-trophy.png'
  },
  
  // Carmen-specific management icons (mapped to existing Supabase storage)
  carmenManagement: {
    // Team size icons
    teamSmall: 'user-role-programs.png',
    teamMedium: 'network-connection.png', 
    teamLarge: 'workflowProcess.png',
    teamDepartment: 'data-analytics.png',
    
    // Performance management icons
    performanceGoals: 'learning-target.png',
    performanceBalance: 'mission-heart.png',
    performanceFeedback: 'communication.png',
    performanceGrowth: 'growth-plant.png',
    performanceMetrics: 'data-analytics.png',
    performanceRecognition: 'achievement-trophy.png',
    performanceProcess: 'workflow-process.png',
    performanceUniform: 'user-role-it.png',
    
    // Retention strategy icons
    retentionDecline: 'data-analytics.png',
    retentionStagnation: 'workflow-process.png',
    retentionWarning: 'learning-target.png',
    retentionBalance: 'mission-heart.png',
    retentionMoney: 'achievement-trophy.png',
    retentionLocked: 'user-role-it.png',
    retentionCulture: 'communication.png',
    retentionConversation: 'communication.png',
    retentionTraining: 'learning-target.png',
    retentionLeadership: 'achievement-trophy.png',
    retentionMentorship: 'network-connection.png',
    retentionHome: 'workflow-process.png',
    retentionRewards: 'achievement-trophy.png',
    retentionRotation: 'growth-plant.png',
    retentionWellness: 'mission-heart.png',
    retentionStats: 'data-analytics.png',
    retentionHeart: 'mission-heart.png',
    retentionUp: 'growth-plant.png',
    retentionTarget: 'learning-target.png',
    retentionStar: 'achievement-trophy.png',
    retentionManager: 'user-role-it.png',
    retentionSpeed: 'workflow-process.png',
    retentionSpeak: 'communication.png',
    retentionTopStar: 'achievement-trophy.png',
    retentionCostMoney: 'data-analytics.png',
    retentionHandshake: 'network-connection.png',
    retentionPath: 'growth-plant.png',
    retentionCrystalBall: 'learning-target.png',
    retentionTeam: 'network-connection.png',
    retentionBrain: 'user-role-it.png',
    retentionRocket: 'workflow-process.png',
    
    // Engagement icons
    engagementSatisfied: 'mission-heart.png',
    engagementRocket: 'workflow-process.png',
    engagementStar: 'achievement-trophy.png',
    engagementSpeed: 'growth-plant.png',
    engagementShining: 'learning-target.png',
    engagementMask: 'communication.png',
    engagementCycle: 'growth-plant.png',
    engagementMedal: 'achievement-trophy.png'
  }
} as const;

// Enhanced dynamic role-specific messaging based on user profile
export const ROLE_MESSAGING = {
  fundraising: {
    examples: 'Quality control for grant proposals, briefing AI for donor communications, turning program reports into funding stories, planning multi-donor campaigns, and building reusable templates for major gift asks',
    successMetric: 'Write stronger grant proposals and connect more effectively with donors',
    welcomeMessage: 'Master AI tools for fundraising success',
    // Dynamic messaging components for personalization
    roleSpecific: {
      beginner: {
        techComfort: {
          beginner: "You'll start with simple AI tools that help write better grant applications and donor emails. Each small win will build your confidence until AI feels like a natural part of your work.",
          intermediate: "You're ready to use AI to write grants that get funded and create donor messages that actually connect. You'll learn the right prompts to get results that sound like you, not a robot.",
          advanced: "You can dive deep into AI tools that research donors, draft compelling proposals, and track what works. You'll build systems that make fundraising easier and more effective."
        }
      },
      little: {
        techComfort: {
          beginner: "You've tried AI before - now you'll turn those experiments into real fundraising wins. You'll create templates and workflows that make every grant application stronger.",
          intermediate: "Your AI experience gives you a head start. You'll learn to build prompt libraries and research systems that help you write better grants faster and connect with donors more personally.",
          advanced: "You're ready to build comprehensive AI systems for fundraising. Think donor research databases, automated follow-up sequences, and strategy tools that help you raise more money."
        }
      },
      lots: {
        techComfort: {
          beginner: "You know AI well - now you'll apply it to fundraising in ways that feel natural and get results. You'll create workflows that help you write better grants without adding complexity to your day.",
          intermediate: "Your AI knowledge combined with fundraising focus puts you in a great spot. You'll design systems that other nonprofits will want to copy - tools that actually help raise more money.",
          advanced: "You're positioned to lead how nonprofits use AI for fundraising. You'll create new approaches to donor research, grant strategy, and relationship building that others will study and follow."
        }
      }
    }
  },
  programs: {
    examples: 'Quality control for program reports, briefing AI for participant communications, extracting impact stories from data, planning program presentations for stakeholders, and building evaluation templates that show real outcomes',
    successMetric: 'Tell more compelling program stories and demonstrate impact more clearly',
    welcomeMessage: 'Enhance your programs with AI insights',
    roleSpecific: {
      beginner: {
        techComfort: {
          beginner: "You'll use AI to track how well your programs work and show their impact. Simple tools will help you collect better data and write reports that funders actually want to read.",
          intermediate: "You're ready to use AI for participant tracking, outcome measurement, and creating program stories that show real impact. You'll learn tools that make data collection easier and reporting more compelling.",
          advanced: "You can build AI systems that predict participant success, track outcomes automatically, and optimize how you deliver services. You'll create new standards for what good programs look like."
        }
      },
      little: {
        techComfort: {
          beginner: "You've experimented with AI - now you'll use it to consistently improve participant outcomes and show program impact. You'll build workflows that make data collection and evaluation much easier.",
          intermediate: "Your AI experience puts you ahead of the game. You'll develop evaluation systems and participant engagement tools that improve service delivery while reducing paperwork and admin work.",
          advanced: "You're ready to build AI systems that predict what participants need, personalize interventions, and track outcomes automatically. Think tools that help participants succeed more often."
        }
      },
      lots: {
        techComfort: {
          beginner: "You understand AI well - now you'll apply it to programs in practical ways that improve participant outcomes without making your workflow more complex.",
          intermediate: "Your AI knowledge combined with program experience puts you in a unique position. You'll design service delivery models that other organizations will want to learn from and copy.",
          advanced: "You're positioned to lead how nonprofits use AI for program delivery. You'll create new approaches to participant engagement and outcome measurement that become industry standards."
        }
      }
    }
  },
  operations: {
    examples: 'Quality control for policy documents, briefing AI for staff communications, transforming meeting notes into action plans, directing AI for HR processes, and creating workflow templates that reduce repetitive tasks',
    successMetric: 'Spend less time on admin tasks and more time on strategic work',
    welcomeMessage: 'Streamline operations with AI automation',
    roleSpecific: {
      beginner: {
        techComfort: {
          beginner: "You'll find AI transforms your operational workload naturally, starting with simple automation of repetitive tasks and gradually building systems that free you to focus on strategic initiatives while improving accuracy and efficiency.",
          intermediate: "Your practical approach to technology makes you ideal for operational AI implementation - you'll quickly develop automated workflows for HR, finance, and administrative processes that dramatically reduce manual work while improving organizational effectiveness.",
          advanced: "Your tech enthusiasm will accelerate operational transformation - you'll implement sophisticated automation systems, predictive analytics for resource planning, and AI-powered decision support tools that revolutionize organizational efficiency."
        }
      },
      little: {
        techComfort: {
          beginner: "Building on your AI experiments, you'll create comprehensive operational systems that turn occasional automation into consistent organizational efficiency - developing processes that handle routine tasks while you focus on high-impact strategic work.",
          intermediate: "Your AI experience gives you an advantage in operational innovation - you'll design integrated automation systems that streamline everything from payroll to compliance, creating seamless workflows that boost organizational productivity.",
          advanced: "With your AI foundation and tech skills, you're positioned to architect next-generation operational systems - implementing advanced automation, predictive maintenance, and AI-assisted resource management that sets new efficiency standards."
        }
      },
      lots: {
        techComfort: {
          beginner: "Your extensive AI knowledge provides a strong foundation for operational excellence - you'll implement sophisticated systems in user-friendly ways that transform organizational efficiency while maintaining simplicity in daily operations.",
          intermediate: "Your AI expertise combined with operational focus creates perfect conditions for organizational transformation - you'll design innovative systems that become models for efficient, AI-powered nonprofit operations.",
          advanced: "As an AI expert in operations, you're ready to lead organizational revolution - creating cutting-edge automation, predictive systems, and intelligent workflow management that redefines what's possible in nonprofit efficiency."
        }
      }
    }
  },
  marketing: {
    examples: 'Quality control for social media posts, briefing AI for newsletter content, turning program updates into compelling stories, planning content campaigns that engage supporters, and building message templates for consistent brand voice',
    successMetric: 'Create more engaging content that connects with your audience',
    welcomeMessage: 'Amplify your mission with AI-powered marketing',
    roleSpecific: {
      beginner: {
        techComfort: {
          beginner: "You'll use AI to help tell your organization's story better. Start with tools that help you write social media posts, newsletters, and website content that actually connects with people.",
          intermediate: "You're ready to use AI for content creation and audience engagement. You'll learn tools that help you create consistent, compelling messaging across all your channels without losing your authentic voice.",
          advanced: "You can build AI systems for audience analysis, personalized content creation, and campaign optimization. You'll expand your mission's reach and create content that really resonates with supporters."
        }
      },
      little: {
        techComfort: {
          beginner: "You've tried AI for content - now you'll turn those experiments into consistent audience engagement. You'll create content workflows that amplify your mission while keeping your authentic voice.",
          intermediate: "Your AI experience gives you an edge in marketing. You'll design content strategies and engagement systems that increase your mission's visibility and connect with more supporters.",
          advanced: "You're ready to build AI systems that personalize content, predict what audiences want, and optimize campaigns automatically. Think marketing that actually works harder for your mission."
        }
      },
      lots: {
        techComfort: {
          beginner: "You know AI well - now you'll apply it to marketing in ways that create compelling content and engagement strategies without making your workflow more complicated.",
          intermediate: "Your AI knowledge plus marketing focus puts you in a great position. You'll design communication strategies that other nonprofits will study and want to copy.",
          advanced: "You're positioned to lead how nonprofits do marketing with AI. You'll create new approaches to audience engagement and content personalization that become the new standard."
        }
      }
    }
  },
  it: {
    examples: 'Quality control for technical documentation, briefing AI for system reports, extracting insights from usage data, planning technology rollouts with clear communication, and building help templates that staff actually understand',
    successMetric: 'Implement technology solutions that staff actually embrace',
    welcomeMessage: 'Lead your organization\'s AI adoption',
    roleSpecific: {
      beginner: {
        techComfort: {
          beginner: "You'll help your organization adopt AI thoughtfully, starting with practical tools that improve systems and workflows. You'll build confidence across teams without disrupting how people work.",
          intermediate: "You're in a great position to lead your organization's AI adoption. You'll implement systems that optimize infrastructure and automate processes while keeping everything secure and reliable.",
          advanced: "Your tech skills put you in the perfect spot to lead digital transformation. You'll deploy AI systems that optimize infrastructure and create intelligent automation that sets new standards."
        }
      },
      little: {
        techComfort: {
          beginner: "You've experimented with AI - now you'll create thoughtful technology strategies that balance innovation with practicality. You'll build systems that help the organization while ensuring smooth adoption.",
          intermediate: "Your AI experience gives you advantages in tech leadership. You'll design systems that optimize performance, enhance security, and support growth while keeping operations stable.",
          advanced: "You're ready to lead cutting-edge transformation. You'll implement systems that optimize everything from data management to user experience, making technology work better for everyone."
        }
      },
      lots: {
        techComfort: {
          beginner: "You understand AI deeply - now you'll implement advanced solutions in accessible ways. Your organization will benefit from cutting-edge capabilities while keeping interfaces user-friendly.",
          intermediate: "Your AI expertise plus systems thinking puts you in a unique position. You'll design technology solutions that other nonprofits will want to learn from and implement.",
          advanced: "You're positioned to pioneer the future of nonprofit technology. You'll create new systems and user experiences that redefine what's possible with mission-driven technology."
        }
      }
    }
  },
  leadership: {
    examples: 'Quality control for board communications, briefing AI for strategic presentations, transforming operational data into leadership insights, directing communication campaigns across departments, and building decision-making frameworks that guide your team',
    successMetric: 'Lead with confidence and communicate your vision more effectively',
    welcomeMessage: 'Guide your organization into the AI era',
    roleSpecific: {
      beginner: {
        techComfort: {
          beginner: "You'll lead your organization's AI transformation with wisdom and strategy. You'll implement tools that help with decision-making and strategic planning while making sure your team feels supported through the change.",
          intermediate: "Your leadership perspective plus practical tech approach creates ideal conditions for AI adoption. You'll guide implementation of strategic tools that improve decision-making and organizational effectiveness.",
          advanced: "Your leadership vision and tech enthusiasm position you to pioneer organizational transformation. You'll implement AI for strategic analysis and data-driven decisions that elevate your impact."
        }
      },
      little: {
        techComfort: {
          beginner: "You've experimented with AI - now you'll develop strategic frameworks that turn those experiments into organizational advantage. You'll create leadership approaches that maximize AI benefits while keeping your culture and values.",
          intermediate: "Your AI experience gives you strategic advantages in leadership. You'll design technology strategies that enhance decision-making, improve effectiveness, and drive your mission forward.",
          advanced: "With your AI knowledge and leadership skills, you're positioned to architect organizational transformation. You'll implement strategic systems that optimize planning and decision-making across the organization."
        }
      },
      lots: {
        techComfort: {
          beginner: "Your deep AI knowledge provides a strong foundation for transformational leadership. You'll implement solutions that enhance effectiveness while keeping focus on mission and values.",
          intermediate: "Your AI expertise combined with leadership perspective creates opportunities for breakthrough organizational development. You'll design approaches that become benchmarks for AI-powered nonprofit leadership.",
          advanced: "You're positioned to define the future of mission-driven organizations. You'll create strategic systems and decision frameworks that maximize social impact and become models for others to follow."
        }
      }
    }
  },
  other: {
    examples: 'Quality control for professional communications, briefing AI for role-specific content, extracting value from existing materials, planning strategic communications, and building templates that enhance your unique contributions',
    successMetric: 'Work more effectively and make a bigger impact in your role',
    welcomeMessage: 'Discover AI applications for your work',
    roleSpecific: {
      beginner: {
        techComfort: {
          beginner: "You'll discover how AI naturally enhances your unique contribution to mission work, starting with simple tools that amplify your existing strengths and gradually building capabilities that expand your impact and effectiveness.",
          intermediate: "Your balanced approach to technology makes you ideal for discovering innovative AI applications - you'll quickly identify and implement tools that enhance your specific role while contributing to broader organizational success.",
          advanced: "Your tech enthusiasm will accelerate the discovery of cutting-edge applications for your unique role - you'll implement sophisticated AI solutions that not only enhance your work but potentially create new models for similar positions."
        }
      },
      little: {
        techComfort: {
          beginner: "Building on your AI experiments, you'll develop systematic approaches that turn occasional tool use into consistent enhancement of your unique contributions - creating workflows that amplify your impact while maintaining your authentic approach.",
          intermediate: "Your AI experience positions you to pioneer applications in your specific area - you'll design innovative solutions that dramatically enhance your effectiveness while potentially creating templates for others in similar roles.",
          advanced: "With your AI foundation and growing tech skills, you're positioned to revolutionize your area of expertise - implementing advanced solutions that maximize your unique contributions to mission success."
        }
      },
      lots: {
        techComfort: {
          beginner: "Your extensive AI knowledge provides a strong foundation for innovation in your unique role - you'll apply sophisticated techniques in accessible ways that enhance your contributions while maintaining the personal touch that makes your work special.",
          intermediate: "Your AI expertise combined with your unique perspective creates perfect conditions for breakthrough innovation - you'll design cutting-edge applications that become models for excellence in your area of expertise.",
          advanced: "As an AI expert in your specialized area, you're positioned to lead transformation - creating revolutionary approaches that redefine what's possible in your field while maintaining the mission focus that drives your work."
        }
      }
    }
  }
} as const;

// Cache for icon URLs to prevent repeated generation
const iconUrlCache = new Map<string, string>();

// Clear cache function for when icons are updated
export const clearIconCache = (): void => {
  iconUrlCache.clear();
  console.log('Icon URL cache cleared');
};

/**
 * Get the full Supabase Storage URL for an icon with caching and error handling
 */
export const getSupabaseIconUrl = (iconPath: string): string => {
  // Prevent infinite recursion for fallback
  const isFallback = iconPath === SUPABASE_ICONS.lyraAvatar;

  // Clear cache for role icons to ensure fresh URLs after updates
  if (iconPath.includes('user-role-')) {
    iconUrlCache.delete(iconPath);
  }

  // Check cache first
  if (iconUrlCache.has(iconPath)) {
    return iconUrlCache.get(iconPath)!;
  }

  try {
    const { data } = supabase.storage
      .from('app-icons')
      .getPublicUrl(iconPath);

    // Cache the result
    iconUrlCache.set(iconPath, data.publicUrl);

    // Debug log for avatar files
    if (iconPath.includes('sofia') || iconPath.includes('maya') || iconPath.includes('lyra')) {
      console.log(`Avatar URL generated: ${iconPath} -> ${data.publicUrl}`);
    }

    return data.publicUrl;
  } catch (error) {
    console.error(`Error loading icon: ${iconPath}`, error);
    // Return fallback icon URL (prevent infinite loop)
    if (!isFallback) {
      return getSupabaseIconUrl(SUPABASE_ICONS.lyraAvatar);
    }
    // Last resort fallback
    return '';
  }
};

/**
 * Get animated dashboard rocket URLs for video element
 */
export const getDashboardRocketUrls = () => {
  return {
    mp4: getSupabaseIconUrl(SUPABASE_ICONS.dashboardRocketMp4),
    gif: getSupabaseIconUrl(SUPABASE_ICONS.dashboardRocketGif),
    fallback: getSupabaseIconUrl(SUPABASE_ICONS.dashboardMeditation)
  };
};

/**
 * Get chapter icon URL by chapter ID with fallback
 */
export const getChapterIconUrl = (chapterId: number): string => {
  const iconPath = SUPABASE_ICONS.chapters[chapterId as keyof typeof SUPABASE_ICONS.chapters] || SUPABASE_ICONS.lyraAvatar;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get feature icon URL by feature name with fallback
 */
export const getFeatureIconUrl = (featureName: keyof typeof SUPABASE_ICONS.features): string => {
  const iconPath = SUPABASE_ICONS.features[featureName] || SUPABASE_ICONS.lyraAvatar;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get onboarding icon URL by step name with fallback
 */
export const getOnboardingIconUrl = (stepName: keyof typeof SUPABASE_ICONS.onboarding): string => {
  const iconPath = SUPABASE_ICONS.onboarding[stepName] || SUPABASE_ICONS.navbar.logo;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get role-specific onboarding icon URL
 */
export const getRoleOnboardingIconUrl = (role: string): string => {
  const iconPath = SUPABASE_ICONS.userRoles[role as keyof typeof SUPABASE_ICONS.userRoles] || SUPABASE_ICONS.userRoles.it;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get Lyra expression icon URL by state with fallback
 */
export const getLyraIconUrl = (state: keyof typeof SUPABASE_ICONS.lyra = 'default'): string => {
  console.log(`Getting Lyra icon for state: ${state}`);
  
  const iconPath = SUPABASE_ICONS.lyra[state] || SUPABASE_ICONS.lyra.default;
  
  const url = getSupabaseIconUrl(iconPath);
  console.log(`Lyra icon URL generated: ${url}`);
  return url;
};

/**
 * Get user role avatar URL by role with fallback
 */
export const getUserRoleIconUrl = (role: keyof typeof SUPABASE_ICONS.userRoles): string => {
  const iconPath = SUPABASE_ICONS.userRoles[role] || SUPABASE_ICONS.userRoles.it;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get UI state icon URL by state with fallback
 */
export const getUIStateIconUrl = (state: keyof typeof SUPABASE_ICONS.uiStates): string => {
  const iconPath = SUPABASE_ICONS.uiStates[state] || SUPABASE_ICONS.lyraAvatar;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get achievement badge URL by achievement type with fallback
 */
export const getAchievementIconUrl = (achievement: keyof typeof SUPABASE_ICONS.achievements): string => {
  const iconPath = SUPABASE_ICONS.achievements[achievement] || SUPABASE_ICONS.features.achievementTrophy;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get navbar icon URL by type with fallback
 */
export const getNavbarIconUrl = (type: keyof typeof SUPABASE_ICONS.navbar): string => {
  const iconPath = SUPABASE_ICONS.navbar[type] || SUPABASE_ICONS.navbar.logo;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get character avatar URL by character type with fallback
 */
export const getCharacterAvatarUrl = (characterType: 'lyra' | 'maya' | 'sofia' | 'david' | 'rachel' | 'alex' | 'carmen'): string => {
  const avatarMap = {
    lyra: 'lyra-avatar.png',
    maya: 'maya-avatar.png',
    sofia: 'sofia-avatar.png',
    david: 'lyra-avatar.png', // fallback until david avatar exists
    rachel: 'lyra-avatar.png', // fallback until rachel avatar exists
    alex: 'lyra-avatar.png', // fallback until alex avatar exists
    carmen: 'lyra-avatar.png' // fallback until carmen avatar exists
  };

  return getSupabaseIconUrl(avatarMap[characterType]);
};

/**
 * Get animation URL from the animations folder
 */
export const getAnimationUrl = (animationName: string): string => {
  return getSupabaseIconUrl(`animations/${animationName}`);
};

/**
 * Get Carmen management icon URL by logical name
 */
export const getCarmenManagementIconUrl = (iconName: keyof typeof SUPABASE_ICONS.carmenManagement): string => {
  const iconPath = SUPABASE_ICONS.carmenManagement[iconName] || SUPABASE_ICONS.features.achievementTrophy;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get specialty AI tool icon URL by tool name
 */
export const getSpecialtyToolIconUrl = (toolName: keyof typeof SUPABASE_ICONS.specialtyTools): string => {
  const iconPath = SUPABASE_ICONS.specialtyTools[toolName] || SUPABASE_ICONS.features.learningTarget;
  return getSupabaseIconUrl(iconPath);
};

/**
 * Get role-specific messaging (basic version for backwards compatibility)
 */
export const getRoleMessaging = (role: string) => {
  return ROLE_MESSAGING[role as keyof typeof ROLE_MESSAGING] || ROLE_MESSAGING.other;
};

/**
 * Enhanced dynamic role messaging based on user profile
 */
interface UserProfile {
  role: string;
  aiExperience: 'none' | 'little' | 'lots';
  techComfort: 'beginner' | 'intermediate' | 'advanced';
  learningStyle?: 'visual' | 'hands-on' | 'conceptual' | 'story';
}

export const getDynamicRoleMessaging = (profile: UserProfile) => {
  const roleData = ROLE_MESSAGING[profile.role as keyof typeof ROLE_MESSAGING] || ROLE_MESSAGING.other;
  
  // Map 'none' to 'beginner' for AI experience
  const aiExperience = profile.aiExperience === 'none' ? 'beginner' : profile.aiExperience;
  
  // Get the dynamic message based on AI experience and tech comfort
  const dynamicMessage = roleData.roleSpecific?.[aiExperience as keyof typeof roleData.roleSpecific]
    ?.techComfort?.[profile.techComfort as keyof any];
  
  // Enhance the basic messaging with learning style considerations
  let enhancedMessage = dynamicMessage || roleData.welcomeMessage;
  
  // Add learning style context if available
  if (profile.learningStyle && dynamicMessage) {
    const learningStyleEnhancement = {
      visual: " You'll get visual guides and step-by-step flowcharts that make AI concepts clear and easy to follow.",
      'hands-on': " You'll practice with interactive exercises and immediately apply what you learn to see actual results.",
      conceptual: " You'll get detailed explanations that help you understand not just what to do, but why it works and how to adapt it.",
      story: " You'll see realistic scenarios that mirror challenges nonprofits face, showing how AI can transform similar work situations."
    };
    
    enhancedMessage += learningStyleEnhancement[profile.learningStyle];
  }
  
  return {
    ...roleData,
    enhancedWelcomeMessage: enhancedMessage,
    dynamicDescription: dynamicMessage || roleData.welcomeMessage
  };
};
